import { Lock } from 'lucide-react'

import { NoteMarkdown } from '@/components/NoteMarkdown'
import { formatServiceStatus } from '@/help/serviceStatus'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import type { ServiceStatus } from '@/lib/schemas/db'

export interface StatusCardProps {
  /** e.g. "Callout", "Job" — used on the cancel button. */
  resourceLabel: string
  status: ServiceStatus
  locked?: boolean
  /**
   * The reason behind the current status (from its latest status change).
   * Emphasized when the status is one that traps work — Blocked or Waiting on
   * Parts — so the "why" is unmissable next to the "what".
   */
  statusReason?: string
  /** Renders the "Change status…" button when provided. */
  onOpenChangeStatus?: () => void
  /** Renders the "Cancel {resourceLabel}" button when provided. */
  onOpenCancel?: () => void
  cancelPending?: boolean
  /** Renders the assignee row when provided. */
  assigned?: {
    name?: string
    onOpenAssign: () => void
  }
}

/**
 * A service's status at a glance: no headings, labels or helper copy — each
 * row is a piece of data with its action right beside it. Status sits next to
 * the buttons that change it, the reason (if any) directly underneath, and
 * the assignee next to the button that reassigns.
 */
export function StatusCard(props: StatusCardProps) {
  const {
    resourceLabel,
    status,
    locked = false,
    statusReason,
    onOpenChangeStatus,
    onOpenCancel,
    cancelPending = false,
    assigned,
  } = props

  const reasonTrapsWork = status === 'Blocked' || status === 'WaitingOnParts'

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${getServiceStatusMeta(status).dotClass}`}
          />
          <span
            className={`text-[13px] font-semibold ${getServiceStatusMeta(status).textClass}`}
          >
            {formatServiceStatus(status)}
          </span>
          {locked ? (
            <Lock className="h-3.5 w-3.5 text-slate-500" aria-label="Locked" />
          ) : null}
        </div>
        {onOpenChangeStatus ? (
          <button
            type="button"
            onClick={onOpenChangeStatus}
            className="h-[30px] cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Change status…
          </button>
        ) : null}
        {onOpenCancel ? (
          <button
            type="button"
            disabled={cancelPending}
            onClick={onOpenCancel}
            className="inline-flex h-[30px] cursor-pointer items-center gap-2 rounded-[7px] border border-red-200 bg-white px-3 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {cancelPending ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-red-400 border-t-transparent" />
            ) : null}
            Cancel {resourceLabel}
          </button>
        ) : null}
      </div>
      {statusReason ? (
        <NoteMarkdown
          text={statusReason}
          className={
            reasonTrapsWork
              ? 'text-xs font-bold text-red-600'
              : 'text-xs text-slate-600'
          }
        />
      ) : null}
      {assigned ? (
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`truncate text-[12.5px] ${assigned.name ? 'text-slate-900' : 'text-slate-400'}`}
          >
            {assigned.name ?? 'Unassigned'}
          </span>
          <button
            type="button"
            onClick={assigned.onOpenAssign}
            className="h-[30px] shrink-0 cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            {assigned.name ? 'Reassign…' : 'Assign…'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The reason attached to the most recent transition into the current status —
 * what StatusCard shows under the status line. Older reasons stay in the
 * history.
 */
export function latestStatusReason(
  statusChanges: Array<{ newStatus: string; createdAt: string; reason?: string }>,
  status: string,
): string | undefined {
  const latest = [...statusChanges]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .reverse()
    .find((change) => change.newStatus === status)
  return latest?.reason?.trim() || undefined
}
